import { useQuery } from '@tanstack/react-query';
import { queryPrometheus, queryAlerts } from './prometheusApi';
import { useRefreshInterval } from './RefreshContext';

interface PodInfo {
  pod: string;
  namespace: string;
  phase: string;
  restarts: number;
  ready: boolean;
}

interface MetricResult {
  metric: { [key: string]: string | undefined; pod: string; namespace: string; phase: string };
  value: [number, string];
}

export function usePodStatus() {
  const interval = useRefreshInterval();
  return useQuery({
    queryKey: ['cockpit', 'pods'],
    queryFn: async (): Promise<PodInfo[]> => {
      const [phaseData, restartData, readyData] = await Promise.all([
        queryPrometheus('kube_pod_status_phase{namespace="default"}==1') as Promise<MetricResult[]>,
        queryPrometheus('kube_pod_container_status_restarts_total{namespace="default"}') as Promise<MetricResult[]>,
        queryPrometheus('kube_pod_status_ready{namespace="default",condition="true"}') as Promise<MetricResult[]>,
      ]);

      const restartMap = new Map<string, number>();
      for (const r of restartData) {
        const pod = r.metric.pod;
        const val = Number(r.value[1]);
        restartMap.set(pod, Math.max(restartMap.get(pod) ?? 0, val));
      }

      const readySet = new Set<string>();
      for (const r of readyData) {
        if (Number(r.value[1]) === 1) readySet.add(r.metric.pod);
      }

      return phaseData.map((p) => ({
        pod: p.metric.pod,
        namespace: p.metric.namespace,
        phase: p.metric.phase,
        restarts: restartMap.get(p.metric.pod) ?? 0,
        ready: readySet.has(p.metric.pod),
      }));
    },
    refetchInterval: interval,
  });
}

interface Alert {
  labels: Record<string, string>;
  annotations: Record<string, string>;
  state: string;
  activeAt: string;
}

export function useAlerts() {
  const interval = useRefreshInterval();
  return useQuery({
    queryKey: ['cockpit', 'alerts'],
    queryFn: () => queryAlerts() as Promise<Alert[]>,
    refetchInterval: interval,
  });
}

export interface ClusterMetrics {
  cpuUsage: number;
  cpuTotal: number;
  memoryUsage: number;
  memoryTotal: number;
  podCount: number;
  diskUsage: number;
  diskTotal: number;
  pgDiskUsage: number;
  pgDiskTotal: number;
  dbTransactionsPerSec: number;
  dbSizeBytes: number;
}

export function useClusterMetrics() {
  const interval = useRefreshInterval();
  return useQuery({
    queryKey: ['cockpit', 'metrics'],
    queryFn: async (): Promise<ClusterMetrics> => {
      const [cpu, cpuCap, mem, memCap, pods, disk, diskCap, pgDisk, pgDiskCap, dbTx, dbSize] = await Promise.all([
        queryPrometheus('sum(rate(node_cpu_seconds_total{mode!="idle"}[5m]))') as Promise<MetricResult[]>,
        queryPrometheus('sum(machine_cpu_cores)') as Promise<MetricResult[]>,
        queryPrometheus('sum(node_memory_MemTotal_bytes - node_memory_MemAvailable_bytes)') as Promise<MetricResult[]>,
        queryPrometheus('sum(node_memory_MemTotal_bytes)') as Promise<MetricResult[]>,
        queryPrometheus('count(kube_pod_status_phase{namespace="default",phase="Running"}==1)') as Promise<MetricResult[]>,
        queryPrometheus('sum(node_filesystem_size_bytes{mountpoint="/"} - node_filesystem_avail_bytes{mountpoint="/"})') as Promise<MetricResult[]>,
        queryPrometheus('sum(node_filesystem_size_bytes{mountpoint="/"})') as Promise<MetricResult[]>,
        queryPrometheus('kubelet_volume_stats_used_bytes{namespace="default",persistentvolumeclaim="data-postgresql-primary-0"}') as Promise<MetricResult[]>,
        queryPrometheus('kubelet_volume_stats_capacity_bytes{namespace="default",persistentvolumeclaim="data-postgresql-primary-0"}') as Promise<MetricResult[]>,
        queryPrometheus('sum(rate(pg_stat_database_xact_commit{datname=~"storage|keycloak"}[5m]))') as Promise<MetricResult[]>,
        queryPrometheus('sum(pg_database_size_bytes{datname=~"storage|keycloak"})') as Promise<MetricResult[]>,
      ]);

      return {
        cpuUsage: Number(cpu[0]?.value[1] ?? 0),
        cpuTotal: Number(cpuCap[0]?.value[1] ?? 1),
        memoryUsage: Number(mem[0]?.value[1] ?? 0),
        memoryTotal: Number(memCap[0]?.value[1] ?? 1),
        podCount: Number(pods[0]?.value[1] ?? 0),
        diskUsage: Number(disk[0]?.value[1] ?? 0),
        diskTotal: Number(diskCap[0]?.value[1] ?? 1),
        pgDiskUsage: Number(pgDisk[0]?.value[1] ?? 0),
        pgDiskTotal: Number(pgDiskCap[0]?.value[1] ?? 1),
        dbTransactionsPerSec: Number(dbTx[0]?.value[1] ?? 0),
        dbSizeBytes: Number(dbSize[0]?.value[1] ?? 0),
      };
    },
    refetchInterval: interval,
  });
}

export interface EndpointStatus {
  instance: string;
  up: boolean;
}

export function useEndpointStatus() {
  const interval = useRefreshInterval();
  return useQuery({
    queryKey: ['cockpit', 'endpoints'],
    queryFn: async (): Promise<EndpointStatus[]> => {
      const results = await queryPrometheus('probe_success{job="probe/default/website-endpoints"}') as MetricResult[];
      return results.map((r) => ({
        instance: (r.metric as any).instance ?? '',
        up: Number(r.value[1]) === 1,
      }));
    },
    refetchInterval: interval,
  });
}

export interface ServiceStatus {
  name: string;
  up: boolean;
}

const SERVICE_NAMES: Record<string, string> = {
  'http://storage-storage.default.svc.cluster.local:8881/health': 'Storage',
  'http://authorization-authorization.default.svc.cluster.local:8882/health': 'Authorization',
  'http://payment-payment.default.svc.cluster.local:8883/health': 'Payment',
  'http://mailer-mailer.default.svc.cluster.local:3280/mailer/liveness': 'Mailer',
};

export function useServiceHealth() {
  const interval = useRefreshInterval();
  return useQuery({
    queryKey: ['cockpit', 'services'],
    queryFn: async (): Promise<ServiceStatus[]> => {
      const results = await queryPrometheus('probe_success{job="probe/default/service-health"}') as MetricResult[];
      return results.map((r) => ({
        name: SERVICE_NAMES[(r.metric as any).instance ?? ''] ?? (r.metric as any).instance ?? '',
        up: Number(r.value[1]) === 1,
      }));
    },
    refetchInterval: interval,
  });
}

export interface KeycloakMetrics {
  loginsPerHour: number;
  logoutsPerHour: number;
  failedLoginsPerHour: number;
}

export function useKeycloakMetrics() {
  const interval = useRefreshInterval();
  return useQuery({
    queryKey: ['cockpit', 'keycloak'],
    queryFn: async (): Promise<KeycloakMetrics> => {
      const [logins, logouts, failed] = await Promise.all([
        queryPrometheus('sum(increase(keycloak_logins[1h]))') as Promise<MetricResult[]>,
        queryPrometheus('sum(increase(keycloak_user_event_LOGOUT[1h]))') as Promise<MetricResult[]>,
        queryPrometheus('sum(increase(keycloak_failed_login_attempts[1h]))') as Promise<MetricResult[]>,
      ]);

      return {
        loginsPerHour: Math.round(Number(logins[0]?.value[1] ?? 0)),
        logoutsPerHour: Math.round(Number(logouts[0]?.value[1] ?? 0)),
        failedLoginsPerHour: Math.round(Number(failed[0]?.value[1] ?? 0)),
      };
    },
    refetchInterval: interval,
  });
}
